import React, { useEffect, useState, useRef } from 'react';
import HeroSection from '../components/HeroSection';
import TreatmentCaseSlider from '../components/TreatmentCaseSlider';
import { useLocation, useNavigate } from 'react-router-dom';

const ImplantPage = () => {
  // URL 파라미터에서 초기 탭 설정 (깜빡임 방지)
  const location = useLocation();
  const navigate = useNavigate();
  
  const getInitialTab = () => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    return tabParam || 'custom'; // 기본값: 'custom'
  };

  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [sliderHeight, setSliderHeight] = useState('auto');
  const heroRef = useRef(null);
  const slideRefs = useRef([]);

  // URL 변경 시 탭 상태 업데이트
  useEffect(() => {
    const urlParams = new URLSearchParams(location.search);
    const tabParam = urlParams.get('tab');
    const newTab = tabParam || 'custom';
    setActiveTab(newTab);
  }, [location.search]);

  useEffect(() => {
    // fullPage.js 제거 (혹시 남아있을 경우)
    if (window.$ && window.$.fn && window.$.fn.fullpage && window.$.fn.fullpage.destroy) {
      window.$.fn.fullpage.destroy('all');
    }
    
    // body에서 fullpage-enabled 클래스 제거
    document.body.classList.remove('fullpage-enabled');
    
    // 일반 스크롤 복원을 위한 스타일 추가
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';
    
    // cleanup
    return () => {
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
    };
  }, []);

  // activeTab이 변경될 때마다 페이지를 맨 위로 스크롤
  useEffect(() => {
    // DOM 업데이트 후 스크롤하도록 지연
    const timer = setTimeout(() => {
      if (heroRef.current) {
        heroRef.current.scrollIntoView({
          behavior: 'auto',
          block: 'start'
        });
      } else {
        window.scrollTo({
          top: 0,
          behavior: 'auto'
        });
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [activeTab]);

  // 탭이 변경될 때 슬라이드 초기화
  useEffect(() => {
    setCurrentSlide(0);
  }, [activeTab]);

  // 슬라이드 높이 조정
  useEffect(() => {
    const updateSliderHeight = () => {
      const currentSlideElement = slideRefs.current[currentSlide];
      if (currentSlideElement) {
        const slideHeight = currentSlideElement.offsetHeight;
        setSliderHeight(`${slideHeight}px`);
      }
    };

    // DOM 업데이트 후 높이 계산
    const timer = setTimeout(updateSliderHeight, 100);
    
    // 윈도우 리사이즈 시에도 높이 재계산
    window.addEventListener('resize', updateSliderHeight);

    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', updateSliderHeight);
    };
  }, [currentSlide, activeTab]);

  const tabs = [
    { id: 'custom', name: '환자 맞춤형 임플란트', shortName: '맞춤형' },
    { id: 'advanced', name: '고난도 뼈이식 & 상악동 거상 동반 임플란트', shortName: '고난도' },
    { id: 'full-mouth', name: '고난도 전악치료', shortName: '전악치료' },
    { id: 'revision', name: '임플란트 재수술', shortName: '재수술' },
    { id: 'orthodontic', name: '교정 동반 임플란트', shortName: '교정' },
  ];

  // 탭 변경 핸들러 (URL도 함께 업데이트)
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    navigate(`/implant?tab=${tabId}`, { replace: true });
  };

  // 임플란트 재수술 사례 데이터
  const revisionCases = [
    {
      id: '01',
      age: '40대',
      reason: '타치과 임플란트가 흔들리고 고름이 나와요',
      treatment: '임플란트 제거 후 뼈이식 동반 재임플란트 식립',
      beforeImage: '/assets/임플란트/임플란트 재수술/임플란트 재수술_01_치료전.webp',
      afterImage: '/assets/임플란트/임플란트 재수술/임플란트 재수술_01_치료후.webp'
    },
    {
      id: '02',
      age: '60대',
      reason: '타치과 임플란트가 흔들리고 고름이 나와요',
      treatment: '임플란트 제거 후 뼈이식 동반 재임플란트 식립',
      beforeImage: '/assets/임플란트/임플란트 재수술/임플란트 재수술_02_치료전.webp',
      afterImage: '/assets/임플란트/임플란트 재수술/임플란트 재수술_02_치료후.webp'
    }
  ];

  // 교정 동반 임플란트 사례 데이터
  const orthodonticCases = [
    {
      id: '01',
      age: '30대',
      reason: '치아가 없는채로 방치해서 교합이 다 무너졌어요',
      treatment: '전악교정 + 임플란트',
      beforeImage: '/assets/임플란트/교정 동반 임플란트/교정 동반 임플란트_01_치료전.webp',
      duringImage: '/assets/임플란트/교정 동반 임플란트/교정 동반 임플란트_01_치료중.webp',
      afterImage: '/assets/임플란트/교정 동반 임플란트/교정 동반 임플란트_01_치료후.webp'
    },
    {
      id: '02',
      age: '40대',
      reason: '발치한 채로 방치한 자리 치아가 누워서 교정과 임플란트를 하고 싶어요',
      treatment: '부분교정 + 임플란트',
      beforeImage: '/assets/임플란트/교정 동반 임플란트/교정 동반 임플란트_02_치료전.webp',
      duringImage: '/assets/임플란트/교정 동반 임플란트/교정 동반 임플란트_02_치료중.webp',
      afterImage: '/assets/임플란트/교정 동반 임플란트/교정 동반 임플란트_02_치료후.webp'
    }
  ];

  // 고난도 전악치료 사례 데이터
  const fullMouthCases = [
    {
      id: '01',
      age: '50대',
      reason: '어금니가 다 빠져서 밥을 먹을 수 없어요',
      treatment: '전악 복합 치료 (교합 회복 후 임플란트)',
      beforeImage: '/assets/임플란트/고난도 전악치료/전악치료_01_치료전.webp',
      afterImage: '/assets/임플란트/고난도 전악치료/전악치료_01_치료후.webp'
    },
    {
      id: '02',
      age: '60대',
      reason: '윗니 크라운이 다 연결되어 있어서 새로 치료하고 싶어요',
      treatment: '전악 복합 치료 (교합 회복 후 임플란트 + 신경치료,크라운 치료)',
      beforeImage: '/assets/임플란트/고난도 전악치료/전악치료_02_치료전.webp',
      afterImage: '/assets/임플란트/고난도 전악치료/전악치료_02_치료후.webp'
    },
    {
      id: '03',
      age: '30대',
      reason: '치아가 많이 상하고 통증이 심해요',
      treatment: '전악 복합 치료 (신경치료,크라운 + 임플란트 치료)',
      beforeImage: '/assets/임플란트/고난도 전악치료/전악치료_03_치료전.webp',
      afterImage: '/assets/임플란트/고난도 전악치료/전악치료_03_치료후.webp'
    },
    {
      id: '04',
      age: '60대',
      reason: '아래 안좋은 치아를 빼고 임플란트를 하고 싶어요',
      treatment: '상악 전체 틀니 + 하악 임플란트 치료',
      beforeImage: '/assets/임플란트/고난도 전악치료/전악치료_04_치료전.webp',
      afterImage: '/assets/임플란트/고난도 전악치료/전악치료_04_치료후.webp'
    }
  ];

  // 고난도 뼈이식 & 상악동 거상 동반 임플란트 사례 데이터
  const advancedCases = [
    {
      id: '01',
      age: '50대',
      reason: '잇몸뼈가 너무 얇아 임플란트가 힘들다고 대학병원에 가보라고 했어요',
      treatment: '상악동 거상 및 뼈이식 동반 임플란트 치료 (정출 대합치 신경치료 후 크라운)',
      beforeImage: '/assets/임플란트/고난도 뼈이식 & 상악동 거상 동반 임플란트/고난도뼈이식_01_치료전.webp',
      afterImage: '/assets/임플란트/고난도 뼈이식 & 상악동 거상 동반 임플란트/고난도뼈이식_01_치료후.webp',
      beforeDescription: '위 어금니 발치 후 잇몸뼈가 1~2mm 내외로 매우 얇아 임플란트 식립이 어려운 상황',
      afterDescription: '상악동 거상 후 인공뼈 이식을 통해 임플란트를 단단하게 식립'
    },
    {
      id: '02',
      age: '60대',
      reason: '어금니를 발치해서 임플란트 하고 싶어요',
      treatment: '상악동 거상 및 뼈이식 동반 임플란트 치료',
      beforeImage: '/assets/임플란트/고난도 뼈이식 & 상악동 거상 동반 임플란트/고난도뼈이식_02_치료전.webp',
      afterImage: '/assets/임플란트/고난도 뼈이식 & 상악동 거상 동반 임플란트/고난도뼈이식_02_치료후.webp'
    },
    {
      id: '03',
      age: '40대',
      reason: '어금니가 많이 흔들리고 뿌리만 남았어요',
      treatment: '상악동 거상 및 뼈이식 동반 임플란트 치료',
      beforeImage: '/assets/임플란트/고난도 뼈이식 & 상악동 거상 동반 임플란트/고난도뼈이식_03_치료전.webp',
      afterImage: '/assets/임플란트/고난도 뼈이식 & 상악동 거상 동반 임플란트/고난도뼈이식_03_치료후.webp'
    }
  ];

  const renderCustomContent = () => (
    <div className="space-y-16">
      {/* 환자 맞춤형 임플란트 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">환자 맞춤형 임플란트</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">모든 환자의 구강 조건은 다릅니다</p>
          <p className="mb-4">
            <strong className="text-primary">내 잇몸에 딱 맞는 맞춤형 어버트먼트로 완벽한 임플란트를 완성합니다.</strong>
          </p>
          <p className="mb-4">
            기존의 획일적인 어버트먼트가 아닌, <strong className="text-secondary">내 잇몸에 딱</strong> 맞는<br />
            환자의 잇몸 형태와 치아 모양에 정확히 맞춘 맞춤형 어버트먼트를 제작합니다.
          </p>
          <p className="text-lg">
            3차원 CT 분석과 구강 스캔을 통해<br />
            <strong className="text-primary">자연치아와 구별되지 않는 완벽한 임플란트</strong>를 만들어드립니다.
          </p>
        </div>
      </div>

      {/* 맞춤형 어버트먼트 VS 기존 어버트먼트 비교 */}
      <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-3xl p-8 md:p-12">
        <div className="text-center mb-10">
          <h3 className="text-3xl font-bold text-primary mb-4">맞춤형 어버트먼트 VS 기존 어버트먼트</h3>
          <p className="text-lg text-gray-600">차이를 직접 확인해보세요</p>
        </div>

                {/* 모바일 레이아웃 */}
        <div className="md:hidden max-w-4xl mx-auto space-y-6">
          {/* 기존 어버트먼트 */}
          <div className="bg-white rounded-2xl p-6 shadow-lg">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-500 rounded-full mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </div>
              <h4 className="text-2xl font-bold text-gray-800 mb-2">기존 어버트먼트</h4>
              <p className="text-gray-600">획일적인 규격형 어버트먼트</p>
            </div>
            
            <div className="mb-6">
              <img 
                src="/assets/임플란트/환자 맞춤형 임플란트/기존어버트먼트.webp" 
                alt="기존 어버트먼트" 
                className="w-full max-w-full object-contain bg-gray-50 rounded-xl"
                loading="lazy"
                style={{ aspectRatio: 'auto' }}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">잇몸 형태와 맞지 않는 형태</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">부자연스러운 잇몸 윤곽</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">음식물 끼임 현상</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">관리의 어려움</p>
              </div>
            </div>
          </div>

          {/* VS - 두 카드 사이에 위치 */}
          <div className="flex justify-center">
            <div className="w-16 h-16 bg-primary rounded-full flex items-center justify-center shadow-lg">
              <span className="text-white font-bold text-xl">VS</span>
            </div>
          </div>

          {/* 맞춤형 어버트먼트 */}
          <div className="bg-white rounded-2xl p-6 shadow-lg border-2 border-primary">
            <div className="text-center mb-6">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <h4 className="text-2xl font-bold text-primary mb-2">맞춤형 어버트먼트</h4>
              <p className="text-gray-600">환자 개별 맞춤 제작</p>
            </div>
            
            <div className="mb-6">
              <img 
                src="/assets/임플란트/환자 맞춤형 임플란트/맞춤형어버트먼트.webp" 
                alt="맞춤형 어버트먼트" 
                className="w-full max-w-full object-contain bg-blue-50 rounded-xl"
                loading="lazy"
                style={{ aspectRatio: 'auto' }}
              />
            </div>

            <div className="space-y-4">
              <div className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700"><strong>내 잇몸에 딱</strong> 맞는 완벽한 형태</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">자연스러운 잇몸 윤곽 형성</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">음식물 끼임 최소화</p>
              </div>
              <div className="flex items-start">
                <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                <p className="text-gray-700">쉬운 관리와 청결 유지</p>
              </div>
            </div>
          </div>
        </div>

        {/* 데스크톱 레이아웃 */}
        <div className="hidden md:block max-w-6xl mx-auto">
          <div className="relative grid grid-cols-2 gap-12">
            {/* VS 구분선 - 데스크톱용 */}
            <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 w-16 h-16 bg-primary rounded-full flex items-center justify-center z-10 shadow-lg">
              <span className="text-white font-bold text-xl">VS</span>
            </div>

            {/* 기존 어버트먼트 - 데스크톱 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-gray-500 rounded-full mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-gray-800 mb-2">기존 어버트먼트</h4>
                <p className="text-gray-600">획일적인 규격형 어버트먼트</p>
        </div>

              <div className="mb-6">
                <img 
                  src="/assets/임플란트/환자 맞춤형 임플란트/기존어버트먼트.webp" 
                  alt="기존 어버트먼트" 
                  className="w-full max-w-full object-contain bg-gray-50 rounded-xl"
                  loading="lazy"
                  style={{ aspectRatio: 'auto' }}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-gray-700">잇몸 형태와 맞지 않는 형태</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-gray-700">부자연스러운 잇몸 윤곽</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-gray-700">음식물 끼임 현상</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-gray-700">관리의 어려움</p>
                </div>
              </div>
            </div>

            {/* 맞춤형 어버트먼트 - 데스크톱 */}
            <div className="bg-white rounded-2xl p-8 shadow-lg border-2 border-primary">
              <div className="text-center mb-6">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-primary rounded-full mb-4">
                  <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h4 className="text-2xl font-bold text-primary mb-2">맞춤형 어버트먼트</h4>
                <p className="text-gray-600">환자 개별 맞춤 제작</p>
        </div>

              <div className="mb-6">
                <img 
                  src="/assets/임플란트/환자 맞춤형 임플란트/맞춤형어버트먼트.webp" 
                  alt="맞춤형 어버트먼트" 
                  className="w-full max-w-full object-contain bg-blue-50 rounded-xl"
                  loading="lazy"
                  style={{ aspectRatio: 'auto' }}
                />
              </div>

              <div className="space-y-4">
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-gray-700"><strong>내 잇몸에 딱</strong> 맞는 완벽한 형태</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-gray-700">자연스러운 잇몸 윤곽 형성</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-gray-700">음식물 끼임 최소화</p>
                </div>
                <div className="flex items-start">
                  <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                  <p className="text-gray-700">쉬운 관리와 청결 유지</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* 맞춤형 어버트먼트의 장점 */}
      <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">맞춤형 어버트먼트의 특별한 장점</h3>
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-white rounded-lg p-6 text-center shadow-md">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">정밀한 3D 설계</h4>
            <p className="text-gray-600 text-sm">구강 스캔과 CT 데이터를 바탕으로 한 정밀 설계</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 text-center shadow-md">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">자연스러운 모양</h4>
            <p className="text-gray-600 text-sm">자연치아와 구별되지 않는 완벽한 형태</p>
          </div>
          
          <div className="bg-white rounded-lg p-6 text-center shadow-md">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">장기적 안정성</h4>
            <p className="text-gray-600 text-sm">완벽한 맞춤으로 오랜 기간 사용 가능</p>
          </div>
        </div>
      </div>

      {/* 치료 과정 */}
      <div className="bg-gradient-to-r from-blue-50 via-purple-50 to-pink-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">맞춤형 임플란트 치료 과정</h3>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">정밀 진단</h4>
            <p className="text-sm text-gray-600">3차원 CT 촬영 및 구강 스캔</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-indigo-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">맞춤 설계</h4>
            <p className="text-sm text-gray-600">개인별 어버트먼트 3D 설계</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">임플란트 식립</h4>
            <p className="text-sm text-gray-600">정확한 위치에 임플란트 시술</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">맞춤 제작</h4>
            <p className="text-sm text-gray-600">개인별 어버트먼트 제작</p>
      </div>

          <div className="text-center">
            <div className="w-16 h-16 bg-rose-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">5</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">최종 완성</h4>
            <p className="text-sm text-gray-600">맞춤형 크라운 장착</p>
          </div>
        </div>
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">환자 맞춤형 임플란트 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          <strong>내 잇몸에 딱</strong> 맞는 맞춤형 어버트먼트로<br />
          자연치아와 구별되지 않는 완벽한 임플란트를 경험해보세요.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderAdvancedContent = () => (
    <div className="space-y-16">
      {/* 고난도 임플란트 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">고난도 뼈이식 & 상악동 거상 동반 임플란트</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">뼈가 부족해서 임플란트가 불가능하다고 들었나요?</p>
          <p className="mb-4">
            <strong className="text-primary">뼈이식과 상악동 거상술로 불가능을 가능으로 만듭니다.</strong>
          </p>
          <p className="mb-4">
            오랜 기간 치아가 없어서 뼈가 흡수되었거나, 상악동이 확장되어 뼈의 높이가 부족한 경우에도<br />
            <strong className="text-secondary">고난도 뼈이식 기술로 임플란트가 가능합니다.</strong>
          </p>
          <p className="text-lg">
            풍부한 임상경험과 첨단 장비를 바탕으로<br />
            <strong className="text-primary">안전하고 성공적인 고난도 임플란트를 시행합니다.</strong>
          </p>
        </div>

        {/* 고난도 임플란트의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-red-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">뼈이식 전문</h4>
            <p className="text-gray-600 text-sm">부족한 뼈를 재생하여 임플란트 가능</p>
          </div>
          
          <div className="bg-orange-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">상악동 거상술</h4>
            <p className="text-gray-600 text-sm">상악동 공간 확보로 임플란트 식립</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">풍부한 경험</h4>
            <p className="text-gray-600 text-sm">다양한 고난도 케이스 성공 경험</p>
          </div>
        </div>
      </div>

      {/* 치료 사례 */}
      <div>
        <TreatmentCaseSlider 
          cases={advancedCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          slideRefs={slideRefs}
          sliderHeight={sliderHeight}
          showImageDescriptions={true}
          title="치료 사례"
        />
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">고난도 임플란트 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          뼈가 부족해서 임플란트를 포기하셨나요?
          뼈이식과 상악동 거상술로 불가능을 가능으로 만들어드립니다.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );



  const renderRevisionContent = () => (
    <div className="space-y-16">
      {/* 임플란트 재수술 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">임플란트 재수술</h2>
        <div className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">타치과 임플란트가 문제가 있나요?</p>
          <p className="mb-4">
            <strong className="text-primary">실패한 임플란트도 다시 살릴 수 있습니다.</strong>
          </p>
          <p className="mb-4">
            임플란트 주위염, 부정확한 식립 위치, 보철물 문제 등으로<br />
            흔들리거나 염증이 생긴 임플란트를 <strong className="text-secondary">정밀 재수술로 해결</strong>합니다.
          </p>
          <p className="text-lg">
            실패의 원인을 정확히 분석하고<br />
            <strong className="text-primary">뼈이식과 함께 재임플란트로 확실한 결과를 만들어드립니다.</strong>
          </p>
        </div>

        {/* 재수술의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-red-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">실패 원인 분석</h4>
            <p className="text-gray-600 text-sm">정확한 진단으로 재발 방지</p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">뼈이식 재수술</h4>
            <p className="text-gray-600 text-sm">손상된 뼈 재생 후 재식립</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
          </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">세심한 관리</h4>
            <p className="text-gray-600 text-sm">재수술 후 철저한 사후관리</p>
          </div>
        </div>
      </div>

      {/* 치료 과정 */}
      <div className="bg-gradient-to-r from-red-50 to-orange-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">임플란트 재수술 과정</h3>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">실패 원인 진단</h4>
            <p className="text-sm text-gray-600">3차원 CT로 정확한 원인 분석</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">임플란트 제거</h4>
            <p className="text-sm text-gray-600">문제 임플란트 안전 제거</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-yellow-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">염증 치료</h4>
            <p className="text-sm text-gray-600">감염 부위 완전 치료</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">뼈이식</h4>
            <p className="text-sm text-gray-600">부족한 뼈 재생</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">5</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">재임플란트</h4>
            <p className="text-sm text-gray-600">정확한 위치에 재식립</p>
          </div>
        </div>
      </div>

      {/* 치료 사례 */}
      <div>
        <TreatmentCaseSlider 
          cases={revisionCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          slideRefs={slideRefs}
          sliderHeight={sliderHeight}
          title="치료 사례"
        />
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">임플란트 재수술 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          실패한 임플란트 때문에 고민이세요?<br />
          정확한 진단과 체계적인 재수술로 확실한 해결책을 제시해드립니다.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderOrthodonticContent = () => (
    <div className="space-y-16">
      {/* 교정 동반 임플란트 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">교정 동반 임플란트</h2>
        <div className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">오랫동안 비어있는 자리 때문에 치아가 기울었나요?</p>
          <p className="mb-4">
            <strong className="text-primary">교정과 임플란트를 함께하면 완벽한 결과를 얻을 수 있습니다.</strong>
          </p>
          <p className="mb-4">
            치아가 없는 자리를 오래 방치하면 인접 치아가 기울거나 대합치가 내려오게 됩니다.<br />
            <strong className="text-secondary">교정치료로 공간을 확보한 후 임플란트를 식립</strong>하면 최상의 결과를 얻습니다.
          </p>
          <p className="text-lg">
            무너진 교합을 회복하고<br />
            <strong className="text-primary">자연스럽고 기능적인 치아를 되찾아드립니다.</strong>
          </p>
        </div>

        {/* 교정 동반 임플란트의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-purple-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2zM9 9h6v6H9V9z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">공간 확보</h4>
            <p className="text-gray-600 text-sm">기울어진 치아를 바로 세워 공간 확보</p>
          </div>
          
          <div className="bg-blue-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">교합 회복</h4>
            <p className="text-gray-600 text-sm">무너진 교합을 정상으로 회복</p>
          </div>
          
          <div className="bg-green-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">최적 결과</h4>
            <p className="text-gray-600 text-sm">교정과 임플란트의 시너지 효과</p>
          </div>
        </div>
      </div>

      {/* 치료 과정 */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">교정 동반 임플란트 과정</h3>
        <div className="grid md:grid-cols-4 gap-6">
          <div className="text-center">
            <div className="w-16 h-16 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">정밀 진단</h4>
            <p className="text-sm text-gray-600">교합 분석 및 치료 계획 수립</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-blue-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">교정 치료</h4>
            <p className="text-sm text-gray-600">기울어진 치아 교정 및 공간 확보</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-teal-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">임플란트 식립</h4>
            <p className="text-sm text-gray-600">최적의 위치에 임플란트 시술</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">최종 보철</h4>
            <p className="text-sm text-gray-600">교합에 맞는 보철물 제작</p>
          </div>
        </div>
      </div>

      {/* 치료 사례 */}
      <div>
        <TreatmentCaseSlider 
          cases={orthodonticCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          slideRefs={slideRefs}
          sliderHeight={sliderHeight}
          showDuringImage={true}
          title="치료 사례"
        />
      </div>

        {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">교정 동반 임플란트 상담받아보세요</h3>
          <p className="text-lg mb-6 text-white/90">
          비어있는 자리 때문에 치아가 기울어졌나요?<br />
          교정과 임플란트를 함께하면 완벽한 미소를 되찾을 수 있습니다.
          </p>
          <a 
            href="tel:043-534-3434" 
            className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
          >
            <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
              <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
            </svg>
            상담 예약하기
          </a>
        </div>
    </div>
  );

  const renderFullMouthContent = () => (
    <div className="space-y-16">
      {/* 고난도 전악치료 소개 */}
      <div className="text-center">
        <h2 className="text-4xl font-bold text-primary mb-6">고난도 전악치료</h2>
        <div className="text-xl text-gray-600 max-w-5xl mx-auto leading-relaxed mb-8">
          <p className="mb-4 text-2xl font-semibold text-gray-800">"완전히 무너진 구강 기능을 체계적으로 복원"</p>
          <p className="mb-4">
            <strong className="text-primary">치아가 거의 남아있지 않거나 교합이 완전히 무너진 경우</strong><br />
            임플란트, 교정, 보철치료를 종합한 고난도 전악치료입니다.
          </p>
          <p className="mb-4">
            단순한 치아 치료를 넘어서<br />
            <strong className="text-secondary">전체적인 구강 기능과 안면 조화까지 회복</strong>하는 포괄적 치료입니다.
          </p>
          <p className="text-lg">
            다학제적 접근을 통해 치아, 잇몸, 턱관절, 근육의 조화를<br />
            <strong className="text-primary">종합적으로 회복하는 최고 수준의 치료</strong>입니다.
          </p>
        </div>

        {/* 고난도 전악치료의 특징 */}
        <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
          <div className="bg-violet-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">종합적 치료</h4>
            <p className="text-gray-600 text-sm">교정+임플란트+보철 통합</p>
          </div>
          
          <div className="bg-fuchsia-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-fuchsia-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">턱관절 회복</h4>
            <p className="text-gray-600 text-sm">교합과 턱관절 동시 치료</p>
          </div>
          
          <div className="bg-sky-50 rounded-lg p-6">
            <div className="w-12 h-12 bg-sky-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
              </svg>
            </div>
            <h4 className="text-lg font-semibold text-gray-800 mb-2">안면 조화 회복</h4>
            <p className="text-gray-600 text-sm">전체적인 얼굴 비율까지 고려</p>
          </div>
        </div>
      </div>

      {/* 치료 과정 */}
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 rounded-2xl p-8">
        <h3 className="text-2xl font-bold text-center text-primary mb-8">고난도 전악치료 과정</h3>
        <div className="grid md:grid-cols-5 gap-4">
          <div className="text-center">
            <div className="w-16 h-16 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">1</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">종합 진단</h4>
            <p className="text-sm text-gray-600">구강, 턱관절 전체 분석</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">2</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">마스터 플랜</h4>
            <p className="text-sm text-gray-600">통합 치료 순서 계획</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">3</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">1단계 교정</h4>
            <p className="text-sm text-gray-600">기본 교합 정리</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">4</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">복원 치료</h4>
            <p className="text-sm text-gray-600">임플란트+보철 단계적 진행</p>
          </div>
          
          <div className="text-center">
            <div className="w-16 h-16 bg-violet-500 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-lg">5</span>
            </div>
            <h4 className="font-semibold text-gray-800 mb-2">최종 완성</h4>
            <p className="text-sm text-gray-600">전체 교합 최종 조정</p>
          </div>
        </div>
      </div>

      {/* 치료 사례 */}
      <div>
        <TreatmentCaseSlider 
          cases={fullMouthCases}
          currentSlide={currentSlide}
          setCurrentSlide={setCurrentSlide}
          slideRefs={slideRefs}
          sliderHeight={sliderHeight}
          showDuringImage={false}
          title="치료 사례"
        />
      </div>

      {/* CTA 섹션 */}
      <div className="bg-primary text-white rounded-2xl p-10 text-center">
        <h3 className="text-2xl font-semibold mb-4">고난도 전악치료 상담받아보세요</h3>
        <p className="text-lg mb-6 text-white/90">
          완전히 무너진 구강 기능 때문에 고민이세요?<br />
          체계적인 전악치료로 새로운 미소와 건강을 되찾아드립니다.
        </p>
        <a 
          href="tel:043-534-3434" 
          className="inline-flex items-center bg-white text-primary px-8 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
            <path d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548.773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0 01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
          </svg>
          상담 예약하기
        </a>
      </div>
    </div>
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'custom':
        return renderCustomContent();
      case 'advanced':
        return renderAdvancedContent();
      case 'revision':
        return renderRevisionContent();
      case 'orthodontic':
        return renderOrthodonticContent();
      case 'full-mouth':
        return renderFullMouthContent();
      default:
        return renderCustomContent();
    }
  };

  return (
    <div className="implant-page-container">
      {/* 히어로 섹션 */}
      <HeroSection 
        title="임플란트"
        subtitle="잃어버린 치아, 자연치아처럼 되찾아드립니다"
        backgroundImage="/assets/임플란트배경.png"
        brandText="한앤손 부부치과"
        ref={heroRef}
      >
        {/* 탭 네비게이션 */}
        <div className="flex justify-center">
          <div className="bg-white/20 backdrop-blur-md rounded-2xl p-1 inline-flex max-w-full border border-white/30">
            <div className="flex space-x-1 overflow-x-auto scrollbar-hide">
              {tabs.map((tab) => (
                <button
                  key={tab.id}
                  onClick={() => handleTabChange(tab.id)}
                  className={`px-2 md:px-3 py-2 rounded-xl text-sm font-medium transition-all duration-200 whitespace-nowrap min-w-0 flex-shrink-0 ${
                    activeTab === tab.id
                      ? 'bg-white text-primary shadow-md'
                      : 'text-white/90 hover:text-white hover:bg-white/20'
                  }`}
                >
                  <span className="block md:hidden">{tab.shortName}</span>
                  <span className="hidden md:block">{tab.name}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
      </HeroSection>

      {/* 콘텐츠 영역 */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="transition-all duration-300">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default ImplantPage; 